import React, { lazy, Suspense, useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import API from '../../../api/axiosInstance';
import OnsiteAudioRecorder from './OnsiteAudioRecorder';
import OnsiteSignaturePad from './OnsiteSignaturePad';
import logo from '../../../images/logo.png';
import '../../css/OnsiteRegistrationForm.css';
import { isPossiblePhoneNumber } from 'libphonenumber-js';

const CountryCodeSelector = lazy(() => import('./CountryCodeSelector'));

// Used for react-select JS-based styles only (cannot use CSS for those)
const GOLD = '#a67c2c';
const GREEN = '#00573C';

const Field = ({ label, required, children, span = 1 }) => (
  <div style={{ gridColumn: `span ${span}` }}>
    <label className="onsite-form-field__label">
      {label}{required && <span className="onsite-form-field__required">*</span>}
    </label>
    {children}
  </div>
);

const Section = ({ icon, title, subtitle, accent, children, id }) => (
  <section id={id} className="onsite-form-section">
    <div className="onsite-form-section__header">
      <div className="onsite-form-section__icon-wrap" style={{ background: `${accent}18` }}>
        <i className={`fa-solid ${icon}`} style={{ fontSize: '16px', color: accent }} />
      </div>
      <div>
        <h3 className="onsite-form-section__title">{title}</h3>
        {subtitle && <p className="onsite-form-section__subtitle">{subtitle}</p>}
      </div>
    </div>
    <div className="onsite-form-section__body">{children}</div>
  </section>
);


const OnsiteRegistrationForm = () => {
  const [searchParams] = useSearchParams();
  const id       = searchParams.get('id');
  const isCaddie = searchParams.get('type') === 'caddie';
  // const isKiosk  = searchParams.get('kiosk') === 'true'; // Kiosk mode on hold
  const LABEL    = isCaddie ? 'Caddie' : 'Participant';

  const [loading,    setLoading]    = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error,      setError]      = useState('');
  const [submitted,  setSubmitted]  = useState(false);
  const [sigError,           setSigError]           = useState(false);
  const [accommodationError, setAccommodationError] = useState(false);
  const [hotelNameError,     setHotelNameError]     = useState(false);
  const [regulationsError,   setRegulationsError]   = useState(false);
  const [mobileNumberError,  setMobileNumberError]  = useState('');
  // const [countdown, setCountdown] = useState(15); // Kiosk mode on hold
  const audioBlobRef    = useRef(null);
  const tapCount        = useRef(0);
  const tapTimer        = useRef(null);
  const hadSigOnLoadRef = useRef(false);

  useEffect(() => {
    const handler = (e) => {
      if (audioBlobRef.current || submitting) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [submitting]);

  const [caddies,      setCaddies]      = useState([]);
  const [knownCaddies, setKnownCaddies] = useState([]);
  const [selectedCaddieId, setSelectedCaddieId] = useState(null);
  const [knownPhone,   setKnownPhone]   = useState('');

  const [formData, setFormData] = useState({
    firstName: '', lastName: '', scoreboardName: '', height: '',
    staysAtHotel: false, staysAtOlympicVillage: false,
    onsiteHotelName: '', hasAdditionalCaddie: false, additionalCaddyFirstName: '', additionalCaddyLastName: '', mobileDialCode: '+1', mobileNumber: '',
    pronunciationAudioUrl: '', pronunciationComplete: false, pronunciationNotes: '',
    regulationsAcknowledged: false, signatureDataUrl: '',
  });

  useEffect(() => {
    if (!localStorage.getItem('token')) window.location.replace('/admin-login');
  }, []);

  // Kiosk mode on hold — countdown auto-redirect and logo tap disabled
  // useEffect(() => {
  //   if (!submitted || !isKiosk) return;
  //   const interval = setInterval(() => {
  //     setCountdown(prev => {
  //       if (prev <= 1) { clearInterval(interval); navigate('/onsite-kiosk'); return 0; }
  //       return prev - 1;
  //     });
  //   }, 1000);
  //   return () => clearInterval(interval);
  // }, [submitted, isKiosk, navigate]);

  // const handleLogoTap = () => {
  //   tapCount.current += 1;
  //   clearTimeout(tapTimer.current);
  //   tapTimer.current = setTimeout(() => { tapCount.current = 0; }, 2000);
  //   if (tapCount.current >= 3) { tapCount.current = 0; navigate('/onsite-kiosk'); }
  // };

  const handleCornerTap = () => {
    tapCount.current += 1;
    clearTimeout(tapTimer.current);
    tapTimer.current = setTimeout(() => { tapCount.current = 0; }, 2000);
    if (tapCount.current >= 3) { tapCount.current = 0; window.close(); }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!id) { setError('Invalid participant ID'); setLoading(false); return; }
      try {
        const pRes = await API.get(`/ems/participants/${id}`);
        const pd   = pRes.data?.person_data || {};
        const person = pRes.data?.person_id || {};
        // Guest-row attach on backend overwrites dataGrid; lifted copy lives at pd.knownCaddies.
        const knownCaddiesSrc = Array.isArray(pd.knownCaddies)
          ? pd.knownCaddies
          : (Array.isArray(pd.dataGrid?.knownCaddies) ? pd.dataGrid.knownCaddies : []);
        setKnownCaddies(knownCaddiesSrc);
        const savedPresent = Array.isArray(pd.presentCaddies) ? pd.presentCaddies : [];
        setSelectedCaddieId(savedPresent[0]?._id || null);
        setKnownPhone(pd.phoneNumber?.phoneNumber || '');

        // DB stores additionalCaddies as [{ firstName, lastName }]. Derive UI checkbox state
        // from array content (not length) — empty object in array must not pre-tick checkbox.
        const additionalCaddiesArr   = Array.isArray(pd.additionalCaddies) ? pd.additionalCaddies : [];
        const additionalCaddyEntry   = additionalCaddiesArr[0] || {};
        const hasAdditionalCaddie    = !!(additionalCaddyEntry.firstName || additionalCaddyEntry.lastName);

        setFormData(prev => ({
          ...prev,
          firstName:              pd.givenFirstName      || person.name  || '',
          lastName:               pd.familyLastName      || person.lname || '',
          scoreboardName:         pd.scoreboardName      || '',
          height:                 pd.height              || '',
          staysAtHotel:           pd.staysAtHotel          ?? (!!pd.onsiteHotelName),
          staysAtOlympicVillage:  pd.staysAtOlympicVillage ?? false,
          onsiteHotelName:        pd.onsiteHotelName     || '',
          hasAdditionalCaddie,
          additionalCaddyFirstName: additionalCaddyEntry.firstName || '',
          additionalCaddyLastName:  additionalCaddyEntry.lastName  || '',
          mobileDialCode:         pd.mobileDialCode      || '+1',
          mobileNumber:           pd.mobileNumber        || '',
          pronunciationAudioUrl:  pd.pronunciationAudioUrl  || '',
          pronunciationComplete:  pd.pronunciationComplete  || false,
          pronunciationNotes:     pd.pronunciationNotes  || '',
          regulationsAcknowledged: pd.regulationsAcknowledged || false,
          signatureDataUrl:       pd.signatureDataUrl    || '',
        }));
        hadSigOnLoadRef.current = !!(pd.signatureDataUrl?.startsWith('data:'));

        const compArr = pRes.data?.competition_id;
        const compId  = Array.isArray(compArr)
          ? (compArr[0]?._id || compArr[0])?.toString()
          : (compArr?._id || compArr)?.toString();
        const cRes = await API.get('/ems/participants/caddies', {
          params: compId ? { competitionId: compId } : {},
        });
        setCaddies((cRes.data || []).map(p => ({ value: p._id, label: p.label || '(unnamed)' })));
      } catch (err) {
        if (err.response?.status === 401) {
          localStorage.removeItem('token');
          window.location.replace('/admin-login');
          return;
        }
        setError(`Failed to load: ${err.response?.data?.message || err.message}`);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const setField = useCallback((k, v) => setFormData(prev => ({ ...prev, [k]: v })), []);

  const handleSelectCaddie = useCallback((id) => {
    setSelectedCaddieId(prev => prev === id ? null : id);
  }, []);

  const padReadOnly = hadSigOnLoadRef.current;

  const handleSignatureChange = useCallback((dataUrl) => {
    setField('signatureDataUrl', dataUrl);
    if (dataUrl) setSigError(false);
  }, [setField]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setField(name, type === 'checkbox' ? checked : value);
  };

  const noAccommodation = !formData.staysAtHotel && !formData.staysAtOlympicVillage;
  const noHotelName     = formData.staysAtHotel && !formData.onsiteHotelName.trim();

  const handleSubmit = async () => {
    const noSig           = !padReadOnly && !formData.signatureDataUrl;
    const noRegs          = !formData.regulationsAcknowledged;

    setAccommodationError(noAccommodation);
    setHotelNameError(noHotelName);
    setSigError(noSig);
    setRegulationsError(noRegs);

    let firstErrorId = null;
    if (noAccommodation || noHotelName) firstErrorId = 'basic';
    else if (noSig || noRegs) firstErrorId = 'signature';

    if (firstErrorId) {
      document.getElementById(firstErrorId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    tapCount.current = 0;
    setSubmitting(true);
    setError('');
    try {
      let audioUrl = formData.pronunciationAudioUrl;
      if (audioBlobRef.current) {
        if (formData.pronunciationAudioUrl) {
          try { await API.delete('/ems/uploads/audio', { data: { url: formData.pronunciationAudioUrl } }); } catch { /* ignore */ }
        }
        const blob = audioBlobRef.current;
        const ext  = blob.type.includes('mp4') ? 'mp4' : blob.type.includes('wav') ? 'wav' : 'webm';
        const file = new File([blob], `pronunciation-${Date.now()}.${ext}`, { type: blob.type });
        const fd   = new FormData();
        fd.append('file', file);
        fd.append('participantId', id);
        if (formData.firstName) fd.append('firstName', formData.firstName);
        if (formData.lastName)  fd.append('lastName',  formData.lastName);
        const res = await API.post('/ems/uploads/audio', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        audioUrl = res.data.url;
        audioBlobRef.current = null;
      }

      await API.put(`/ems/participants/${id}`, {
        person_data: {
          givenFirstName:          formData.firstName,
          familyLastName:          formData.lastName,
          scoreboardName:          formData.scoreboardName,
          height:                  formData.height,
          staysAtHotel:            formData.staysAtHotel,
          staysAtOlympicVillage:   formData.staysAtOlympicVillage,
          onsiteHotelName:         formData.onsiteHotelName,
          // Store as array for scalability. Empty array when unchecked or names blank — no boolean saved to DB.
          additionalCaddies:   formData.hasAdditionalCaddie && (formData.additionalCaddyFirstName || formData.additionalCaddyLastName)
                                 ? [{ firstName: formData.additionalCaddyFirstName, lastName: formData.additionalCaddyLastName }]
                                 : [],
          // Caddie physically present today. At most one — single-select radio UI.
          presentCaddies:      knownCaddies.filter(c => c._id === selectedCaddieId),
          mobileDialCode:          formData.mobileDialCode,
          mobileNumber:            formData.mobileNumber,
          pronunciationAudioUrl:   audioUrl,
          pronunciationComplete:   !!audioUrl,
          pronunciationNotes:      formData.pronunciationNotes,
          regulationsAcknowledged: formData.regulationsAcknowledged,
          signatureDataUrl:        formData.signatureDataUrl,
        },
      });
      setSubmitted(true);
    } catch (err) {
      setError(`Save failed: ${err.response?.data?.message || err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteUrl = useCallback(async () => {
    setField('pronunciationAudioUrl', '');
    try {
      await API.put(`/ems/participants/${id}`, {
        person_data: {
          givenFirstName:          formData.firstName,
          familyLastName:          formData.lastName,
          scoreboardName:          formData.scoreboardName,
          height:                  formData.height,
          staysAtHotel:            formData.staysAtHotel,
          staysAtOlympicVillage:   formData.staysAtOlympicVillage,
          onsiteHotelName:         formData.onsiteHotelName,
          // Same array mapping as handleSubmit — keeps DB consistent on audio delete.
          additionalCaddies:   formData.hasAdditionalCaddie && (formData.additionalCaddyFirstName || formData.additionalCaddyLastName)
                                 ? [{ firstName: formData.additionalCaddyFirstName, lastName: formData.additionalCaddyLastName }]
                                 : [],
          presentCaddies:      knownCaddies.filter(c => c._id === selectedCaddieId),
          mobileDialCode:          formData.mobileDialCode,
          mobileNumber:            formData.mobileNumber,
          pronunciationAudioUrl:   '',
          pronunciationComplete:   false,
          pronunciationNotes:      formData.pronunciationNotes,
          regulationsAcknowledged: formData.regulationsAcknowledged,
          signatureDataUrl:        formData.signatureDataUrl,
        },
      });
    } catch { /* silent — S3 already gone, DB clear is best-effort */ }
  }, [id, formData, setField, knownCaddies, selectedCaddieId]);


  if (loading) return (
    <div className="onsite-form-overlay">
      <div className="onsite-form-overlay__spinner-wrap">
        <Spinner animation="border" style={{ color: GOLD, width: '36px', height: '36px' }} />
      </div>
    </div>
  );

  if (submitting) return (
    <div className="onsite-form-overlay">
      <div className="onsite-form-overlay__spinner-wrap">
        <Spinner animation="border" style={{ color: GOLD, width: '36px', height: '36px' }} />
      </div>
      <div style={{ textAlign: 'center' }}>
        <div className="onsite-form-overlay__title">Saving Registration…</div>
        <div className="onsite-form-overlay__caption">Please do not close or go back from this page.</div>
      </div>
    </div>
  );

  if (submitted) return (
    <div className="onsite-form-page">
      <div className="onsite-form-success">
        <div className="onsite-form-success__inner">
          <div className="onsite-form-success__icon">
            <i className="fa-solid fa-check" style={{ fontSize: '36px', color: '#fff' }} />
          </div>
          <h2 className="onsite-form-success__title">{LABEL} Registration Complete</h2>
          <p className="onsite-form-success__sub">{formData.firstName} {formData.lastName} has been successfully registered.</p>
          <p className="onsite-form-success__sub">Please hand the tablet back to an IGF staff member.</p>
        </div>
        <div onClick={handleCornerTap} style={{ position: 'fixed', bottom: 0, left: 0, width: '160px', height: '160px' }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,0.006)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'} />
      </div>
    </div>
  );

  return (
    <div className="onsite-form-page">
      <div className="onsite-form-body">

        <HeroBanner firstName={formData.firstName} lastName={formData.lastName} logo={logo} />

        {error && (
          <div className="onsite-form-error">
            <i className="fa-solid fa-circle-exclamation" style={{ marginRight: '8px' }} />{error}
          </div>
        )}

        {/* BASIC DETAILS */}
        <Section id="basic" icon="fa-user" title="Details" subtitle={`Confirm ${LABEL.toLowerCase()} information`} accent={GOLD}>
          <div className="onsite-form-grid">
            <Field label="First Name" required>
              <input className="onsite-form__input" name="firstName" value={formData.firstName} onChange={handleChange} placeholder="Enter first name" maxLength={20} />
            </Field>
            <Field label="Last Name" required>
              <input className="onsite-form__input" name="lastName" value={formData.lastName} onChange={handleChange} placeholder="Enter last name" maxLength={20} />
            </Field>
            <Field label="Scoreboard Name">
              <input className="onsite-form__input" name="scoreboardName" value={formData.scoreboardName} onChange={handleChange} placeholder="Name displayed on scoreboard" maxLength={20} />
            </Field>
            <Field label="Height (cm)">
              <input className="onsite-form__input" name="height" value={formData.height} onChange={(e) => setField('height', e.target.value.replace(/\D/g, ''))} placeholder="e.g. 180" inputMode="numeric" />
            </Field>
            <Field label="Accommodation" required>
              <div className="onsite-form-segment">
                <button
                  type="button"
                  className={`onsite-form-segment__option${formData.staysAtHotel && !formData.staysAtOlympicVillage ? ' onsite-form-segment__option--active' : ''}`}
                  aria-pressed={formData.staysAtHotel && !formData.staysAtOlympicVillage}
                  onClick={() => { setFormData(prev => ({ ...prev, staysAtHotel: true, staysAtOlympicVillage: false })); setAccommodationError(false); }}
                >
                  <i className="fa-solid fa-hotel" /> Hotel
                </button>
                <button
                  type="button"
                  className={`onsite-form-segment__option${!formData.staysAtHotel && formData.staysAtOlympicVillage ? ' onsite-form-segment__option--active' : ''}`}
                  aria-pressed={!formData.staysAtHotel && formData.staysAtOlympicVillage}
                  onClick={() => { setFormData(prev => ({ ...prev, staysAtHotel: false, staysAtOlympicVillage: true, onsiteHotelName: '' })); setAccommodationError(false); setHotelNameError(false); }}
                >
                  <i className="fa-solid fa-city" /> Olympic Village
                </button>
                <button
                  type="button"
                  className={`onsite-form-segment__option${formData.staysAtHotel && formData.staysAtOlympicVillage ? ' onsite-form-segment__option--active' : ''}`}
                  aria-pressed={formData.staysAtHotel && formData.staysAtOlympicVillage}
                  onClick={() => { setFormData(prev => ({ ...prev, staysAtHotel: true, staysAtOlympicVillage: true })); setAccommodationError(false); }}
                >
                  <i className="fa-solid fa-layer-group" /> Both
                </button>
              </div>
              {accommodationError && (
                <p className="onsite-form-sig-error">
                  <i className="fa-solid fa-triangle-exclamation" />
                  Please select an accommodation option.
                </p>
              )}
            </Field>
            {formData.staysAtHotel && (
              <div>
                <label className="onsite-form-field__label">Hotel Name<span className="onsite-form-field__required">*</span></label>
                <input
                  className="onsite-form__input"
                  name="onsiteHotelName"
                  value={formData.onsiteHotelName}
                  onChange={(e) => { handleChange(e); if (e.target.value.trim()) setHotelNameError(false); }}
                  placeholder="e.g. Marriott Downtown"
                />
                {hotelNameError && (
                  <p className="onsite-form-sig-error">
                    <i className="fa-solid fa-triangle-exclamation" />
                    Hotel name is required.
                  </p>
                )}
                <p className="onsite-form-field__hint">Please let us know where we can reach you</p>
              </div>
            )}
            {!isCaddie && (
              <div style={{ gridColumn: '1 / -1' }}>
                <CaddieSelectionList
                  caddies={knownCaddies}
                  selectedCaddieId={selectedCaddieId}
                  onSelectCaddie={handleSelectCaddie}
                />
              </div>
            )}
            {/* DISABLED — additional caddie section hidden; re-enable when needed
            {!isCaddie && (
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontFamily: '"Poppins", sans-serif', fontSize: '14px', color: '#5c4518', fontWeight: 500 }}>
                  <input
                    type="checkbox"
                    checked={formData.hasAdditionalCaddie}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      hasAdditionalCaddie: e.target.checked,
                      ...(e.target.checked ? {} : { additionalCaddyFirstName: '', additionalCaddyLastName: '' }),
                    }))}
                    style={{ width: '18px', height: '18px', accentColor: '#5c4518', cursor: 'pointer' }}
                  />
                  Do you have an additional caddie to add?
                </label>
              </div>
            )}
            {!isCaddie && formData.hasAdditionalCaddie && (
              <Field label="Additional Caddie First Name">
                <input className="onsite-form__input" value={formData.additionalCaddyFirstName} onChange={(e) => setFormData(prev => ({ ...prev, additionalCaddyFirstName: e.target.value }))} placeholder="First name…" />
              </Field>
            )}
            {!isCaddie && formData.hasAdditionalCaddie && (
              <Field label="Additional Caddie Last Name">
                <input className="onsite-form__input" value={formData.additionalCaddyLastName} onChange={(e) => setFormData(prev => ({ ...prev, additionalCaddyLastName: e.target.value }))} placeholder="Last name…" />
              </Field>
            )}
            */}
            <div style={{ gridColumn: '1' }}>
              <label className="onsite-form-field__label">Telephone Number</label>
              <div className="onsite-form-field__known-phone">
                <span className="onsite-form-field__known-phone-value">{knownPhone || '—'}</span>
              </div>
            </div>
            <Field label="Additional Number During Tournament">
              <div
                style={{
                  display: 'flex', alignItems: 'stretch',
                  border: '2px solid rgba(92,69,24,0.2)', borderRadius: 10,
                  background: '#fffef9', overflow: 'visible',
                  transition: 'border-color 0.15s, box-shadow 0.15s',
                }}
                onFocus={e => { e.currentTarget.style.borderColor = '#a67c2c'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(166,124,44,0.22)'; }}
                onBlur={e => { if (!e.currentTarget.contains(e.relatedTarget)) { e.currentTarget.style.borderColor = 'rgba(92,69,24,0.2)'; e.currentTarget.style.boxShadow = 'none'; } }}
              >
                <Suspense fallback={<div style={{ width: 90, flexShrink: 0 }} />}>
                  <CountryCodeSelector
                    value={formData.mobileDialCode}
                    onChange={(dial) => {
                      setField('mobileDialCode', dial);
                      if (formData.mobileNumber) {
                        setMobileNumberError(
                          !isPossiblePhoneNumber(dial + formData.mobileNumber)
                            ? 'Please enter a valid phone number for the selected country.'
                            : ''
                        );
                      }
                    }}
                    merged
                  />
                </Suspense>
                <div style={{ width: 1, background: 'rgba(92,69,24,0.15)', alignSelf: 'stretch', flexShrink: 0 }} />
                <input
                  name="mobileNumber"
                  value={formData.mobileNumber}
                  onChange={(e) => {
                    const raw = e.target.value.replace(/[^\d()\- ]/g, '');
                    setField('mobileNumber', raw);
                    if (mobileNumberError) setMobileNumberError('');
                  }}
                  onBlur={(e) => {
                    const val = e.target.value;
                    if (val && !isPossiblePhoneNumber(formData.mobileDialCode + val)) {
                      setMobileNumberError('Please enter a valid phone number for the selected country.');
                    }
                  }}
                  placeholder="Enter phone number"
                  inputMode="numeric"
                  style={{
                    flex: 1, border: 'none', outline: 'none', background: 'transparent',
                    padding: '12px 14px', fontSize: 14, fontWeight: 500,
                    color: '#1e1a14', fontFamily: '"Poppins", sans-serif', minWidth: 0,
                  }}
                />
              </div>
              {mobileNumberError && (
                <p className="onsite-form-sig-error">
                  <i className="fa-solid fa-triangle-exclamation" />
                  {mobileNumberError}
                </p>
              )}
            </Field>
          </div>
        </Section>

        {/* NAME PRONUNCIATION */}
        <Section id="onsite" icon="fa-microphone" title="Name Pronunciation" subtitle="Please click to record and crop as needed" accent="#c0392b">
          <div style={{ marginBottom: '20px' }}>
            <div className="onsite-form-audio-label">Please record your name here so media will pronounce it correctly</div>
            <OnsiteAudioRecorder
              existingUrl={formData.pronunciationAudioUrl}
              onBlobReady={(blob) => { audioBlobRef.current = blob; }}
              onDeleteUrl={handleDeleteUrl}
            />
          </div>
        </Section>

        {/* DIGITAL SIGNATURE */}
        <Section id="signature" icon="fa-pen-nib" title="Please sign here" subtitle="Sign the on-site agreement below" accent={GOLD}>
          <OnsiteSignaturePad
            onChange={handleSignatureChange}
            initialDataUrl={formData.signatureDataUrl}
            readOnly={padReadOnly}
          />
          {sigError && (
            <p className="onsite-form-sig-error">
              <i className="fa-solid fa-triangle-exclamation" />
              Signature required before registering.
            </p>
          )}
          <label className="onsite-form-regs">
            <input
              type="checkbox"
              name="regulationsAcknowledged"
              checked={formData.regulationsAcknowledged}
              onChange={(e) => { handleChange(e); if (e.target.checked) setRegulationsError(false); }}
              style={{ width: '22px', height: '22px', accentColor: GREEN, marginTop: '2px', flexShrink: 0, cursor: 'pointer' }}
            />
            <span className="onsite-form-regs__text">
              I have read, understood, and agree to the IGF regulations. By signing above, I acknowledge my acceptance of the terms outlined.
            </span>
          </label>
          {regulationsError && (
            <p className="onsite-form-sig-error">
              <i className="fa-solid fa-triangle-exclamation" />
              Please acknowledge the regulations before registering.
            </p>
          )}
        </Section>

        <div className="onsite-form-footer">
          <button
            onClick={handleSubmit}
            disabled={submitting || noAccommodation || noHotelName}
            className="onsite-form-footer__btn onsite-form-footer__btn--ready"
          >
            {submitting
              ? <><Spinner animation="border" size="sm" /> Saving…</>
              : <><i className="fa-solid fa-circle-check" /> Register {LABEL} <i className="fa-solid fa-arrow-right" /></>
            }
          </button>
        </div>
      </div>
      <div onClick={handleCornerTap}
           style={{ position: 'fixed', bottom: 0, left: 0, width: '160px', height: '160px' }}
           onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,0.006)'}
           onMouseLeave={e => e.currentTarget.style.background = 'transparent'} />
    </div>
  );
};

const CaddieSelectionList = ({ caddies = [], selectedCaddieId, onSelectCaddie }) => {
  const [expanded, setExpanded] = React.useState(new Set());

  const toggleExpand = (e, id) => {
    e.stopPropagation();
    setExpanded(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  return (
    <div>
      <label className="onsite-form-field__label">Caddies Present</label>
      <p style={{ fontSize: '11px', color: '#8a7d6a', margin: '2px 0 10px', fontFamily: '"Poppins", sans-serif' }}>
        Select the caddie who is present today
      </p>
      {caddies.length === 0 ? (
        <p style={{ fontSize: '13px', color: '#8a7d6a', fontFamily: '"Poppins", sans-serif', margin: '8px 0 0' }}>
          No caddies registered for this participant.
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {caddies.map(c => {
            const isSelected = selectedCaddieId === c._id;
            const isExpanded = expanded.has(c._id);
            return (
              <div
                key={c._id}
                style={{
                  borderRadius: '10px',
                  border: `2px solid ${isSelected ? '#a67c2c' : 'rgba(92,69,24,0.15)'}`,
                  background: isSelected ? 'rgba(166,124,44,0.08)' : '#fffef9',
                  transition: 'border-color 0.15s, background 0.15s',
                  overflow: 'hidden',
                  fontFamily: '"Poppins", sans-serif',
                }}
              >
                {/* Row */}
                <div
                  onClick={() => onSelectCaddie?.(c._id)}
                  style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', cursor: 'pointer' }}
                >
                  <div style={{
                    width: '20px', height: '20px', borderRadius: '50%', flexShrink: 0,
                    border: `2px solid ${isSelected ? '#a67c2c' : 'rgba(92,69,24,0.3)'}`,
                    background: 'transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all 0.15s',
                  }}>
                    {isSelected && <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#a67c2c' }} />}
                  </div>
                  <span style={{ fontSize: '14px', fontWeight: 500, color: '#1e1a14', flex: 1 }}>
                    {`${c.name} ${c.lname}`}
                  </span>
                  {isSelected && (
                    <span style={{
                      fontSize: '11px', fontWeight: 700, color: '#a67c2c',
                      background: 'rgba(166,124,44,0.12)', borderRadius: '20px',
                      padding: '2px 10px', letterSpacing: '0.04em',
                    }}>
                      PRESENT
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={(e) => toggleExpand(e, c._id)}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px',
                      color: '#a67c2c', fontSize: '13px', lineHeight: 1, flexShrink: 0,
                      transition: 'transform 0.2s',
                      transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    }}
                  >
                    <i className="fa-solid fa-chevron-down" />
                  </button>
                </div>

                {/* Expanded details */}
                {isExpanded && (
                  <div style={{
                    borderTop: '1px solid rgba(92,69,24,0.1)',
                    padding: '12px 16px 14px',
                    display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px',
                    background: 'rgba(92,69,24,0.03)',
                  }}>
                    {[
                      { label: 'First Name', value: c.name  },
                      { label: 'Last Name',  value: c.lname },
                      { label: 'Email',      value: c.email },
                    ].map(({ label, value }) => (
                      <div key={label}>
                        <div style={{ fontSize: '10px', fontWeight: 800, color: '#8a7d6a', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '3px' }}>{label}</div>
                        <div style={{ fontSize: '13px', fontWeight: 500, color: '#2a2418' }}>{value}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const HeroBanner = ({ firstName, lastName, logo }) => {
  const now = new Date();
  const dateStr = now.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  const timeStr = now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  return (
    <div className="onsite-form-hero">
      <svg className="onsite-form-hero__pattern" viewBox="0 0 200 200" aria-hidden="true">
        <circle cx="100" cy="100" r="90" fill="none" stroke="#fff" strokeWidth="1"/>
        {Array.from({ length: 32 }).map((_, i) => {
          const a = (i / 32) * Math.PI * 2;
          return <circle key={`o-${i}`} cx={100 + Math.cos(a) * 70} cy={100 + Math.sin(a) * 70} r="3" fill="#fff"/>;
        })}
        {Array.from({ length: 24 }).map((_, i) => {
          const a = (i / 24) * Math.PI * 2;
          return <circle key={`m-${i}`} cx={100 + Math.cos(a) * 45} cy={100 + Math.sin(a) * 45} r="2.5" fill="#fff"/>;
        })}
        {Array.from({ length: 12 }).map((_, i) => {
          const a = (i / 12) * Math.PI * 2;
          return <circle key={`i-${i}`} cx={100 + Math.cos(a) * 22} cy={100 + Math.sin(a) * 22} r="2" fill="#fff"/>;
        })}
      </svg>
      <div className="onsite-form-hero__content">
        <div className="onsite-form-hero__top">
          <span className="onsite-form-hero__eyebrow">On-site Registration</span>
        </div>
        <h2 className="onsite-form-hero__welcome">
          Welcome, <span className="onsite-form-hero__name">{firstName} {lastName}</span>
        </h2>
        <div className="onsite-form-hero__date">
          <i className="fa-solid fa-calendar" style={{ marginRight: '7px' }} />{dateStr} · {timeStr}
          <span className="onsite-form-hero__badge" style={{ marginLeft: '12px' }}>
            <i className="fa-solid fa-flag" /> 2028 Los Angeles Olympics · Golf
          </span>
        </div>
      </div>
      <div className="onsite-form-hero__logo-card">
        <img src={logo} alt="IGF — International Golf Federation" />
      </div>
    </div>
  );
};

export default OnsiteRegistrationForm;
